import * as React from 'react'
import * as ReactDOM from 'react-dom/client'
import { ErrorBoundary } from 'react-error-boundary'
import { useNavigate } from 'react-router-dom'

import { Button } from '../../main/components/button'
import { Checkbox } from '../../main/components/checkbox'
import { DefaultErrorBoundary } from '../../main/components/error-view'
import { Paragraph } from '../../main/components/typography'
import { useShowETF } from '../screener/maps/hooks/use-show-etf'
import * as screenerUtils from '../screener/utils'
import * as tracking from '../shared/tracking'
import { Map } from './components/Map'
import { MapContext } from './components/MapContext'
import { MapSelect } from './components/MapSelect'
import { SearchInput, SearchResult, useSearch } from './components/Search'
import { Topbar } from './components/Topbar'
import { Expand } from './components/Topbar/Expand'
import { Share } from './components/Topbar/Share'
import { Zoom } from './components/Topbar/Zoom'
import { MAP_TYPE_SCREENER } from './constants/constants'
import { SettingsScreener, SettingsScreenerDark } from './constants/settings'
import { useMapData } from './hooks/use-map-data'
import { useMapType } from './hooks/use-map-type'
import * as api from './services/api'
import { MapDataIndustry, MapDataNode, MapDataRoot, MapDataSector, MapSubtypeId } from './types'

export interface MapInitProps {
  initData?: MapDataRoot
  cacheBuster: number
  generate?: boolean
}

function onNodeClick({
  event,
  node,
  industry,
  sector,
}: {
  event: React.MouseEvent<HTMLCanvasElement>
  node?: MapDataNode
  industry?: MapDataIndustry
  sector?: MapDataSector
}) {
  if (node) {
    window.open(`/quote.ashx?t=${node.name}`)
    return
  }

  const filters = screenerUtils.parseScreenerFilters()

  if (industry) {
    filters[screenerUtils.ScreenerFilter.Industry] = [industry.name.replace(/[^a-zA-Z]/g, '').toLowerCase()]
  } else if (sector) {
    delete filters[screenerUtils.ScreenerFilter.Industry]
    filters[screenerUtils.ScreenerFilter.Sector] = [sector.name.replace(/[^a-zA-Z]/g, '').toLowerCase()]
  }

  const query = new URLSearchParams(window.location.search)
  query.set(screenerUtils.ScreenerQuery.Filters, screenerUtils.stringifyScreenerFilters(filters))

  const url = `/screener.ashx?${query}`
  if (event.metaKey) {
    window.open(url)
  } else {
    window.location.href = url
  }
}

function App(props: MapInitProps) {
  const search = useSearch()
  const navigate = useNavigate()
  const { subtype } = useMapType()
  const showETF = useShowETF()
  const mapData = useMapData(
    MAP_TYPE_SCREENER.type,
    subtype.value,
    props.cacheBuster,
    props.initData,
    undefined,
    api.mapBaseData,
    () => api.mapPerfScreener()
  )

  /**
   * Set new subtype to be false on perf data load so we don’t display loading state when fetching
   * new perf data for the same type
   */
  const [isNewDataType, setIsNewDataType] = React.useState(false)
  React.useEffect(() => {
    if (!mapData.perfDataQuery.isFetching) {
      setIsNewDataType(false)
    }
  }, [mapData.perfDataQuery.isFetching])

  const isLoadingInitialBaseData = !mapData.baseDataQuery.isPreviousData && mapData.baseDataQuery.isLoading
  const isLoadingInitialPerf = !mapData.perfDataQuery.isPreviousData && mapData.perfDataQuery.isLoading
  const isInitialLoading = isLoadingInitialBaseData || isLoadingInitialPerf
  const isFetchingNewPerf = isNewDataType && !mapData.perfDataQuery.isLoading && mapData.perfDataQuery.isFetching

  return (
    <>
      <Topbar
        className="border border-[#d3d3d3] bg-[#eee] text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
        navContent={
          <>
            <h2 className="m-0 text-2xs uppercase opacity-50 font-bold">View</h2>
            <Button
              as="a"
              href={`/screener.ashx?${screenerUtils.getQueryForView(screenerUtils.ScreenerView.Maps, [
                screenerUtils.ScreenerAddtionalQuery.ShowEtf,
              ])}`}
              leftContent="chartTreemap"
              theme="transparent"
              className="text-gray-600 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Map
            </Button>
            <Button
              as="a"
              href={`/screener.ashx?${screenerUtils.getQueryForView(screenerUtils.ScreenerView.Bubbles, [
                screenerUtils.ScreenerAddtionalQuery.ShowEtf,
              ])}`}
              leftContent="chartBubble"
              theme="transparent"
              className="text-link"
            >
              Bubbles
            </Button>
          </>
        }
        mapControls={
          <>
            <div className="flex px-2">
              <Expand className="text-link" data={mapData.data} />
              <Share className="text-link" mapType={MAP_TYPE_SCREENER} subtype={subtype} />
            </div>
            <Zoom />
          </>
        }
      >
        <Paragraph size="small" className="leading-tight">
          {MAP_TYPE_SCREENER.description}
          {!showETF.visible && showETF.checked && ' ETF size represents assets under management.'}
        </Paragraph>
      </Topbar>
      <div className="flex h-full">
        <div className="map-sidebar mt-0.5 w-[202px]">
          <div className="border border-[#d3d3d3] bg-[#eee] p-4 dark:border-gray-600 dark:bg-gray-700">
            <MapSelect
              label={<span className="m-0 text-3xs uppercase text-gray-500 dark:text-gray-300">Data type</span>}
              aria-label="Data type"
              theme={FinvizSettings.hasDarkTheme ? 'dark' : 'light'}
              value={subtype.value}
              items={MAP_TYPE_SCREENER.subtypes}
              isLoading={isFetchingNewPerf}
              onChange={(item) => {
                const query = new URLSearchParams(window.location.search)
                if (item.value === MapSubtypeId.DayPerf) {
                  query.delete('st')
                } else {
                  query.set('st', item.value)
                }

                setIsNewDataType(true)
                const stringQuery = query.toString()
                navigate({ pathname: window.location.pathname, search: stringQuery }, { replace: true })
                tracking.trackPageView({ query: stringQuery })
              }}
            />

            {showETF.visible && (
              <div className="my-3 border-b border-[#d3d3d3] pb-2 dark:border-gray-600">
                <Checkbox label="Show ETFs" checked={showETF.checked} onChange={showETF.onChange} />
                {showETF.checked && (
                  <Paragraph as="span" size="tiny" className="block leading-snug text-gray-500 dark:text-gray-400">
                    Size represents assets under management
                  </Paragraph>
                )}
              </div>
            )}

            <div className="mt-4">
              <SearchInput value={search.value} onChange={search.onInputChange} theme="light" />
              <ul className="mt-0.5 divide-y divide-[#d3d3d3] py-2 dark:divide-gray-600">
                {search.results.map((result) => (
                  <SearchResult
                    key={result.name}
                    result={result}
                    onClick={search.onItemClick}
                    onMouseLeave={search.onResultMouseLeave}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-50"
                  />
                ))}
              </ul>
            </div>

            {props.initData && props.initData.duplicateTickers > 0 && (
              <div className="mt-1 border-t border-[#d3d3d3] pt-1 text-gray-500 dark:border-gray-600">
                <Paragraph as="span" size="tiny">
                  Only the ticker with the highest market&nbsp;cap for each company is included.{' '}
                  {props.initData.duplicateTickers}&nbsp;tickers were filtered out.
                </Paragraph>
              </div>
            )}
          </div>
        </div>

        <ErrorBoundary resetKeys={[MAP_TYPE_SCREENER.type, subtype.value]} FallbackComponent={DefaultErrorBoundary}>
          <Map
            className="mb-3 min-h-145 pl-1 pt-0.5"
            isFetchingPerf={isFetchingNewPerf}
            isLoadingData={isInitialLoading}
            data={mapData.data}
            type={MAP_TYPE_SCREENER}
            subtype={subtype}
            settings={FinvizSettings.hasDarkTheme ? SettingsScreenerDark : SettingsScreener}
            onNodeClick={onNodeClick}
            dataHash={mapData.perfDataQuery.data?.hash}
          />
        </ErrorBoundary>
      </div>
    </>
  )
}

const rootEl = document.getElementById('root')
const initDataElement = document.getElementById('map-init-data')!
const initData = JSON.parse(initDataElement.textContent!)

const root = ReactDOM.createRoot(rootEl!)

root.render(
  <MapContext>
    <App {...initData} />
  </MapContext>
)
